import React from "react";
import {
  IonIcon,
} from "@ionic/react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  calendar,
  calendarOutline,
  chevronForward,
  peopleCircle,
  personAdd,
  medicalOutline,
  timeOutline,
  documentsOutline,
  personOutline,
  documentTextOutline,
  checkmarkCircleOutline,
  cloudDownloadOutline,
  card,
} from "ionicons/icons";
import { DialogoConfirmacion } from "../../components/basicsComponents/DialogoConfirmacion/DialogoConfirmacion";
import CustomToast from "../../components/basicsComponents/CustomToast/CustomToast";
import { obtenerNombreDia } from "../../utils/funcionalidadesStrings";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Typography,
  Box,
  Card,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  Button,
  Tooltip,
} from "@mui/joy";

import useMainPage from "./useMainPage";
import { usePacienteForm } from "../../components/formComponents/ModalFormPaciente/useModalFormPaciente";
import ComprobanteTurno from "../../components/formComponents/ComprobanteTurno/ComprobanteTurno";
import WelcomeHeader from "../../components/basicsComponents/WelcomeHeader/WelcomeHeader";
import "./MainPage.css";

const MainPage = (props) => {
  const {
    paciente,
    setPaciente,
    turnoSeleccionado,
    abrirModalCancelarReserva,
    abrirComprobanteTurno,
    abrirModalDetalleTurno,
    listadoTurnos,
    prestadoresRecientes,
    toast,
    mostrarNotificacion,
    handleClickRedirigir,
    handleClickSeleccionarTurno,
    handleClickCancelarReserva,
    handleClickComprobanteTurno,
    handleClickReservarTurnoPrestador,
    setAbrirModalCancelarReserva,
    toggleModalDetalleTurno,
    togleAbrirCerrarComprobanteTurno,
  } = useMainPage();

  // Estados locales para acordeones
  const [mostrarPerfil, setMostrarPerfil] = React.useState(false);
  const [editandoPerfil, setEditandoPerfil] = React.useState(false);
  const [mostrarProximoTurno, setMostrarProximoTurno] = React.useState(listadoTurnos.length > 0);
  const [mostrarPrestadores, setMostrarPrestadores] = React.useState(false);
  const [pacienteState, setPacienteState] = React.useState(paciente);

  React.useEffect(() => {
    if (paciente) {
      setPacienteState(paciente);
    }
  }, [paciente]);

  // Mostrar automáticamente el acordeón de Próximos Turnos si hay turnos pendientes
  React.useEffect(() => {
    if (listadoTurnos.length > 0) {
      setMostrarProximoTurno(true);
    }
  }, [listadoTurnos]);

  // Hook para el formulario de paciente (edición de perfil)
  const {
    nombre, setNombre,
    apellido, setApellido,
    correo, setCorreo,
    fechaNac, setFechaNac,
    celular, setCelular,
    nroDocumento, setNroDocumento,
    tiposDoc,
    mutuales,
    mutualAfiliado, setMutualAfiliado,
    tipoDoc, setTipoDoc,
    mutual, setMutual,
    error,
    handleFechaChange,
    handleClickRegistrarPaciente,
  } = usePacienteForm({ 
    paciente: pacienteState, 
    setPaciente: setPaciente, 
    setOpenModal: setMostrarPerfil, 
    mostrarNotificacion, 
    openModal: mostrarPerfil 
  });

  // Handlers para mostrar/ocultar acordeones
  const handleMostrarPerfil = () => setMostrarPerfil(true);
  const handleCerrarPerfil = () => {
    setMostrarPerfil(false);
    setEditandoPerfil(false);
  };
  const handleMostrarProximoTurno = () => setMostrarProximoTurno(true);
  const handleCerrarProximoTurno = () => setMostrarProximoTurno(false);
  const handleMostrarPrestadores = () => setMostrarPrestadores(true);
  const handleCerrarPrestadores = () => setMostrarPrestadores(false);

  // Handlers para edición de perfil
  const handleEditarPerfil = () => setEditandoPerfil(true);
  
  const handleCancelarEdicionPerfil = () => {
    setEditandoPerfil(false);
    // Restaurar valores originales del paciente
    if (paciente) {
      setNombre(paciente.nombre || "");
      setApellido(paciente.apellido || "");
      setCorreo(paciente.email || "");
      if (paciente.nacimiento) {
        const fechaBackend = paciente.nacimiento.split("T")[0];
        const [year, month, day] = fechaBackend.split("-");
        setFechaNac(`${day}/${month}/${year}`);
      } else {
        setFechaNac("");
      }
      setCelular(paciente.celular || "");
      setNroDocumento(paciente.documentoNro || "");
      setMutualAfiliado(paciente.mutualAfiliado || "");
      setTipoDoc(paciente.documentoTipo || -1);
      setMutual(paciente.mutual || -1);
    }
  };
  
  const handleGuardarPerfil = async () => {
    try {
      const guardado = await handleClickRegistrarPaciente();
      if (guardado) {
        setEditandoPerfil(false);
        setMostrarPerfil(false);
      }
    } catch (error) {
      console.error('Error al guardar perfil:', error);
    }
  };

  // Función helper para obtener la clase CSS del ícono según el estado
  const getTurnoIconClass = (estado) => {
    return estado === 'Confirmado' ? 'tech-turno-icon-confirmed' : 'tech-turno-icon-pending';
  };

  return (
    <div className="tech-dashboard-container">
      <div className="tech-dashboard-background"></div>
      {/* Header de bienvenida */}
      <WelcomeHeader paciente={paciente} />
      {/* Estadísticas rápidas */}
      <div className="tech-stats-container">
        <div className="tech-stats-grid">
          <Card className="tech-stat-card" onClick={handleMostrarPerfil} style={{ cursor: 'pointer' }}>
            <Typography className="tech-stat-number">
              <IonIcon icon={personOutline} style={{ fontSize: '2rem', color: 'var(--color-primary)' }} />
            </Typography>
            <Typography className="tech-stat-label">Mi perfil</Typography>
          </Card>
          <Tooltip title="Haz click para ver tus turnos vigentes" placement="top" arrow>
            <Card className="tech-stat-card" onClick={handleMostrarProximoTurno} style={{ cursor: 'pointer' }}>
              <Typography className="tech-stat-number">{listadoTurnos.length}</Typography>
              <Typography className="tech-stat-label">Próximos Turnos</Typography>
            </Card>
          </Tooltip>
          <Tooltip title="Haz click aquí para ver tus últimas consultas" placement="top" arrow>
            <Card className="tech-stat-card" onClick={handleMostrarPrestadores} style={{ cursor: 'pointer' }}>
              <Typography className="tech-stat-number">{prestadoresRecientes.length}</Typography>
              <Typography className="tech-stat-label">Turnos Recientes</Typography>
            </Card>
          </Tooltip>
        </div>
      </div>

      {paciente && mostrarPerfil && (
        <div className="tech-perfil-container" style={{ maxWidth: '1100px', margin: '24px auto' }}>
          <Card className="tech-section">
            <AccordionGroup className="tech-accordion">
              <Accordion defaultExpanded={true}>
                <AccordionSummary>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={2}>
                      <div className="tech-action-icon" style={{ width: '40px', height: '40px', fontSize: '18px', background: 'var(--gradient-secondary)' }}>
                        <IonIcon icon={personOutline} />
                      </div>
                      <div>
                        <Typography level="title-sm" className="tech-prestador-title">
                          Mi perfil
                        </Typography>
                        <Typography level="body-xs" className="tech-prestador-subtitle">
                          Información personal
                        </Typography>
                      </div>
                    </Box>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={e => { e.stopPropagation(); handleCerrarPerfil(); }}
                      sx={{ minWidth: '80px', fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      Cerrar
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Card className="tech-interactive" sx={{ background: 'rgba(102, 126, 234, 0.05) !important', border: '1px solid rgba(102, 126, 234, 0.1) !important', borderRadius: 'var(--radius-md) !important', padding: 'var(--spacing-md) !important' }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Campos no editables - Solo lectura */}
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Nombre:</Typography>
                          <Typography level="body-sm" sx={{ color: '#666' }}>{nombre || paciente?.nombre || '-'}</Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Apellido:</Typography>
                          <Typography level="body-sm" sx={{ color: '#666' }}>{apellido || paciente?.apellido || '-'}</Typography>
                        </Box>
                      </Box>
                      
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Tipo de documento:</Typography>
                          <Typography level="body-sm" sx={{ color: '#666' }}>
                            {tiposDoc.find(doc => doc.codigo === tipoDoc)?.nombre || paciente?.documentoTipoNombre || '-'}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Nro de documento:</Typography>
                          <Typography level="body-sm" sx={{ color: '#666' }}>{nroDocumento || paciente?.documentoNro || '-'}</Typography>
                        </Box>
                      </Box>

                      {/* Campo editable - Fecha de nacimiento */}
                      <Box display="flex" gap={2}>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Fecha de nacimiento:</Typography>
                          <input
                            type="text"
                            value={fechaNac || (paciente?.nacimiento ? dayjs(paciente.nacimiento).format('DD/MM/YYYY') : '')}
                            onChange={handleFechaChange}
                            disabled={!editandoPerfil}
                            placeholder="dd/mm/yyyy"
                            maxLength={10}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: error ? '1px solid red' : '1px solid #ccc', background: editandoPerfil ? 'white' : '#f5f5f5', cursor: editandoPerfil ? 'text' : 'not-allowed' }}
                          />
                          {error && <Typography level="body-xs" sx={{ color: 'red', mt: 0.5 }}>Formato inválido (dd/mm/yyyy)</Typography>}
                        </Box>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Celular:</Typography>
                          <input
                            type="text"
                            value={celular || paciente?.celular || ''}
                            onChange={e => setCelular(e.target.value)}
                            disabled={!editandoPerfil}
                            placeholder="Celular"
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', background: editandoPerfil ? 'white' : '#f5f5f5', cursor: editandoPerfil ? 'text' : 'not-allowed' }}
                          />
                        </Box>
                        <Box flex={1}>
                          <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Correo:</Typography>
                          <input
                            type="email"
                            value={correo || paciente?.email || ''}
                            onChange={e => setCorreo(e.target.value)}
                            disabled={!editandoPerfil}
                            placeholder="Correo"
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc', background: editandoPerfil ? 'white' : '#f5f5f5', cursor: editandoPerfil ? 'text' : 'not-allowed' }}
                          />
                        </Box>
                      </Box>

                      {/* Campos no editables - Obra social y Nro de afiliado (solo si existe paciente) */}
                      {paciente?.codigo && (
                        <Box display="flex" gap={2}>
                          <Box flex={1}>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Obra social:</Typography>
                            <Typography level="body-sm" sx={{ color: '#666' }}>
                              {mutuales.find(mut => mut.codigo === mutual)?.nombre || paciente?.mutualNombre || '-'}
                            </Typography>
                          </Box>
                          <Box flex={1}>
                            <Typography level="body-xs" sx={{ mb: 0.5, fontWeight: 500 }}>Número de afiliado:</Typography>
                            <Typography level="body-sm" sx={{ color: '#666' }}>{mutualAfiliado || paciente?.mutualAfiliado || '-'}</Typography>
                          </Box>
                        </Box>
                      )}

                      <Box display="flex" gap={2} marginTop={2} flexWrap="wrap" justifyContent="flex-end">
                        {!editandoPerfil ? (
                          <Button 
                            variant="soft" 
                            color="primary" 
                            onClick={handleEditarPerfil} 
                            sx={{ minWidth: '120px' }}
                          >
                            Editar
                          </Button>
                        ) : (
                          <>
                            <Button 
                              variant="outlined" 
                              color="danger" 
                              onClick={handleCancelarEdicionPerfil} 
                              sx={{ minWidth: '120px' }}
                            >
                              Salir
                            </Button>
                            <Button 
                              variant="solid" 
                              color="success" 
                              onClick={handleGuardarPerfil} 
                              sx={{ minWidth: '140px' }} 
                              disabled={error}
                            >
                              Grabar y salir
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </AccordionDetails>
              </Accordion>
            </AccordionGroup>
          </Card>
        </div>
      )}

      {listadoTurnos.length > 0 && mostrarProximoTurno && (
        <div className="tech-proximoturno-container" style={{ maxWidth: '1100px', margin: '24px auto' }}>
          <Card className="tech-section">
            <AccordionGroup className="tech-accordion">
              <Accordion defaultExpanded={true}>
                <AccordionSummary>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={2}>
                      <div className="tech-action-icon" style={{ width: '40px', height: '40px', fontSize: '18px', background: 'var(--gradient-secondary)' }}>
                        <IonIcon icon={calendarOutline} />
                      </div>
                      <div>
                        <Typography level="title-sm" className="tech-prestador-title">
                          Próximo Turno
                        </Typography>
                        <Typography level="body-xs" className="tech-prestador-subtitle">
                          Pendiente de asistencia
                        </Typography>
                      </div>
                    </Box>
                    <Button
                      variant="outlined"
                      size="sm"
                      onClick={e => { e.stopPropagation(); handleCerrarProximoTurno(); }}
                      sx={{ minWidth: '80px', fontSize: '0.75rem', padding: '4px 12px' }}
                    >
                      Cerrar
                    </Button>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="grid" gap={2}>
                    {/* Mostrar todos los turnos pendientes */}
                    {listadoTurnos.map((turno, idx) => (
                      <Card key={idx} className="tech-interactive" sx={{ background: 'rgba(102, 126, 234, 0.05) !important', border: '1px solid rgba(102, 126, 234, 0.1) !important', borderRadius: 'var(--radius-md) !important', padding: 'var(--spacing-md) !important' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography level="title-xs" className="tech-prestador-title" style={{ color: '#b71c1c', fontWeight: 600 }}>
                              {turno.profesional || turno.prestadorNom || 'Profesional'} - {turno.especialidadNom || turno.especialidad || 'Especialidad'}
                            </Typography>
                            <Typography level="body-xs" className="tech-prestador-fecha" style={{ fontWeight: 600 }}>
                              {`${dayjs(turno.fecha).format('dddd D [de] MMMM, YYYY [a las]')} ${dayjs(turno.hora).format('HH:mm')}`}
                            </Typography>
                            <Typography level="body-xs" className="tech-prestador-mutual" style={{ color: '#1a237e', fontWeight: 500 }}>
                              {turno.tipo || turno.mutualNom || 'Tipo'}
                            </Typography>
                            <Typography level="body-xs" className="tech-prestador-estado" style={{ color: '#263238', fontWeight: 500 }}>
                              Estado: {turno.estado || 'Pendiente'}
                            </Typography>
                          </Box>
                          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                            <Button
                              variant="soft"
                              size="sm"
                              startDecorator={<IonIcon icon={documentsOutline} style={{ color: '#b71c1c', fontSize: '18px' }} />}
                              onClick={e => { e.stopPropagation(); handleClickComprobanteTurno(turno); }}
                              sx={{ color: '#b71c1c', fontWeight: 600, textTransform: 'none', fontSize: '0.9rem', minWidth: '120px' }}
                            >
                              Comprobante
                            </Button>
                            <Button
                              variant="soft"
                              size="sm"
                              startDecorator={<IonIcon icon={timeOutline} style={{ color: '#b71c1c', fontSize: '18px' }} />}
                              onClick={e => { e.stopPropagation(); handleClickSeleccionarTurno(turno); }}
                              sx={{ color: '#b71c1c', fontWeight: 600, textTransform: 'none', fontSize: '0.9rem', minWidth: '120px' }}
                            >
                              Cancelar
                            </Button>
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </AccordionGroup>
          </Card>
        </div>
      )}
      
      {prestadoresRecientes.length !== 0 && mostrarPrestadores && (
        <div className="tech-prestadores-container">
          <Card className="tech-section">
            <AccordionGroup className="tech-accordion">
            <Accordion defaultExpanded={true}>
              <AccordionSummary>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center" gap={2}>
                    <div className="tech-action-icon" style={{ 
                      width: '40px', 
                      height: '40px', 
                      fontSize: '18px',
                      background: 'var(--gradient-secondary)'
                    }}>
                      <IonIcon icon={peopleCircle} />
                    </div>
                    <div>
                      <Typography level="title-sm" className="tech-prestador-title">
                     Turnos recientes 
                      </Typography>
                      <Typography level="body-xs" className="tech-prestador-subtitle">
                        {prestadoresRecientes.length} especialistas consultados
                      </Typography>
                    </div>
                  </Box>
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCerrarPrestadores();
                    }}
                    sx={{
                      minWidth: '80px',
                      fontSize: '0.75rem',
                      padding: '4px 12px'
                    }}
                  >
                    Cerrar
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="grid" gap={2}>
                  {prestadoresRecientes.map((prestador, index) => (
                    <Card 
                      key={index}
                      sx={{
                        background: 'rgba(102, 126, 234, 0.05) !important',
                        border: '1px solid rgba(102, 126, 234, 0.1) !important',
                        borderRadius: 'var(--radius-md) !important',
                        padding: 'var(--spacing-md) !important'
                      }}
                    >
                      <Box>
                        <Typography level="title-xs" className="tech-prestador-title">
                          {prestador.prestadorNom || 'Especialista'}
                        </Typography>
                        <Typography level="body-xs" className="tech-prestador-subtitle">
                          {prestador.especialidadNom || 'Consulta General'}
                        </Typography>
                        {prestador.sedeNom && (
                          <Typography level="body-xs" className="tech-prestador-sede">
                            Sede: {prestador.sedeNom}
                          </Typography>
                        )}
                        {prestador.fecha && (
                          <Typography level="body-xs" className="tech-prestador-fecha">
                            Último turno: {dayjs(prestador.fecha).format('DD/MM/YYYY')}
                          </Typography>
                        )}
                        {prestador.mutualNom && (
                          <Typography level="body-xs" className="tech-prestador-mutual">
                            <IonIcon icon={card} style={{ marginRight: '4px', fontSize: '12px', color: 'var(--primary-color)' }} />
                            Mutual: {prestador.mutualNom}
                          </Typography>
                        )}
                        <Button
                          variant="solid"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[DEBUG] Botón Reservar turno clickeado, prestador:', prestador);
                            handleClickReservarTurnoPrestador(prestador);
                          }}
                          sx={{
                            background: 'var(--gradient-primary)',
                            borderRadius: 'var(--radius-md)',
                            padding: '6px 16px',
                            fontWeight: '600',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            minWidth: '100px',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #0056b3 0%, #1e40af 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                            }
                          }}
                        >
                          Reservar turno
                        </Button>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </AccordionGroup>
        </Card>
        </div>
      )}
<br/>
      {/* Grid principal de acciones */}
      <div className="tech-dashboard-grid">
        {/* Card Reservar Turno */}
        <Card 
          className="tech-action-card reservar fade-in"
          onClick={() => handleClickRedirigir("Reservar turno")}
        >
          <div className="tech-action-icon">
            <IonIcon icon={personAdd} />
          </div>
          <div className="tech-action-content">
            <Typography className="tech-action-title">
              Reservar Nuevo Turno
            </Typography>
            <Typography className="tech-action-subtitle">
              Programa una nueva cita médica
            </Typography>
          </div>
          <div className="tech-action-arrow">
            <IonIcon icon={chevronForward} />
          </div>
        </Card>

        {/* Card Consultar Turnos */}
        <Card 
          className="tech-action-card turnos fade-in"
          onClick={() => handleClickRedirigir("Turnos")}
        >
          <div className="tech-action-icon">
            <IonIcon icon={calendar} />
          </div>
          <div className="tech-action-content">
            <Typography className="tech-action-title">
              Historial de Turnos
            </Typography>
            <Typography className="tech-action-subtitle">
              Ver el historial de citas programadas
            </Typography>
          </div>
          <div className="tech-action-arrow">
            <IonIcon icon={chevronForward} />
          </div>
        </Card>

        {/* Card Estudios - Temporalmente oculto */}
        {/* <Card 
          className="tech-action-card estudios fade-in"
          onClick={() => handleClickRedirigir("Estudios")}
        >
          <div className="tech-action-icon">
            <IonIcon icon={documentsOutline} />
          </div>
          <div className="tech-action-content">
            <Typography className="tech-action-title">
              Mis Estudios
            </Typography>
            <Typography className="tech-action-subtitle">
              Consultar resultados médicos
            </Typography>
          </div>
          <div className="tech-action-arrow">
            <IonIcon icon={chevronForward} />
          </div>
        </Card> */}

        {/* Card adicional - Horarios */}
        <Card 
          className="tech-action-card fade-in"
          onClick={() => handleClickRedirigir("Horarios")}
        >
          <div className="tech-action-icon">
            <IonIcon icon={timeOutline} />
          </div>
          <div className="tech-action-content">
            <Typography className="tech-action-title">
              Horarios de Atención
            </Typography>
            <Typography className="tech-action-subtitle">
              Consultar disponibilidad médica
            </Typography>
          </div>
          <div className="tech-action-arrow">
            <IonIcon icon={chevronForward} />
          </div>
        </Card>
      </div>

      <>
        <DialogoConfirmacion
          titulo="Cancelar turno"
          texto={`Ha decidido cancelar el turno con el/la profesional ${
            turnoSeleccionado.prestadorNom
          }, con especialidad en ${
            turnoSeleccionado.especialidadNom
          }, para el dia ${obtenerNombreDia(
            dayjs(turnoSeleccionado.fecha).format("dddd")
          )} ${dayjs(turnoSeleccionado.fecha).format("DD")} de ${dayjs(
            turnoSeleccionado.fecha
          ).format("MMMM, YYYY")} a las ${dayjs(turnoSeleccionado.hora).format(
            "HH:mm"
          )}, ¿Esta seguro/a de ello?`}
          open={abrirModalCancelarReserva}
          setOpen={setAbrirModalCancelarReserva}
          handleClickConfirmar={handleClickCancelarReserva}
          colorBotonNo="danger"
          colorBotonSi="success"
          textoBotonNo="No"
          textoBotonSi="Si, cancelo"
        />

        <ComprobanteTurno
          turno={turnoSeleccionado}
          abrir={abrirComprobanteTurno}
          togleAbrirCerrarComprobanteTurno={togleAbrirCerrarComprobanteTurno}
          paciente={paciente}
        />
      </>

      {/* Modal de detalle del turno */}
      <Modal 
        open={abrirModalDetalleTurno} 
        onClose={toggleModalDetalleTurno}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}
      >
        <ModalDialog
          className="tech-modal-detalle-turno"
        >
          <ModalClose 
            sx={{ 
              top: '1rem', 
              right: '1rem',
              zIndex: 10,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--color-danger)',
              '&:hover': {
                backgroundColor: 'var(--color-danger)',
                color: 'white'
              }
            }} 
          />
          
          <DialogTitle
            sx={{
              background: 'var(--gradient-primary)',
              color: 'white',
              margin: '-1.5rem -1.5rem 1.5rem -1.5rem',
              padding: '2rem 1.5rem',
              textAlign: 'center',
              fontSize: '1.5rem',
              fontWeight: '700',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                pointerEvents: 'none'
              }
            }}
          >
            <IonIcon 
              icon={medicalOutline} 
              style={{ 
                fontSize: '2rem', 
                marginRight: '0.5rem',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }} 
            />
            Detalles del Turno
          </DialogTitle>

          <div className="tech-modal-content">
            {/* Información del prestador */}
            <Card 
              className="tech-detalle-section"
              sx={{
                marginBottom: '1.5rem',
                border: '1px solid rgba(102, 126, 234, 0.1)',
                borderRadius: 'var(--radius-lg)',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                <div className="tech-action-icon" style={{ 
                  width: '60px', 
                  height: '60px', 
                  fontSize: '24px',
                  background: 'var(--gradient-primary)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                }}>
                  <IonIcon icon={personOutline} />
                </div>
                <Box flex={1}>
                  <Typography level="title-lg" sx={{ 
                    color: 'var(--color-text-primary)',
                    fontWeight: '700',
                    marginBottom: '0.25rem'
                  }}>
                    {turnoSeleccionado?.prestadorNom || 'Especialista no especificado'}
                  </Typography>
                  <Typography level="body-sm" sx={{ 
                    color: 'var(--color-primary)',
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}>
                    {turnoSeleccionado?.especialidadNom || 'Especialidad no especificada'}
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Información de fecha y hora */}
            <Card 
              className="tech-detalle-section"
              sx={{
                marginBottom: '1.5rem',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                borderRadius: 'var(--radius-lg)',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(16, 185, 129, 0.05) 100%)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <div className="tech-action-icon" style={{ 
                  width: '50px', 
                  height: '50px', 
                  fontSize: '20px',
                  background: 'var(--gradient-success)'
                }}>
                  <IonIcon icon={calendarOutline} />
                </div>
                <Box flex={1}>
                  <Typography level="title-md" sx={{ 
                    color: 'var(--color-text-primary)',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    Fecha y Hora
                  </Typography>
                  <Typography level="body-md" sx={{ 
                    color: 'var(--color-success)',
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    {dayjs(turnoSeleccionado?.fecha).format('dddd, DD [de] MMMM [de] YYYY')}
                  </Typography>
                  <Typography level="body-md" sx={{ 
                    color: 'var(--color-success)',
                    fontWeight: '600',
                    fontSize: '1.1rem',
                    marginTop: '0.25rem'
                  }}>
                    {turnoSeleccionado?.hora ? dayjs(turnoSeleccionado.hora, 'HH:mm').format('HH:mm') : 'Hora no especificada'} hs
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Estado del turno */}
            <Card 
              className="tech-detalle-section"
              sx={{
                marginBottom: '1.5rem',
                border: `1px solid ${
                  turnoSeleccionado?.estado?.toLowerCase() === 'confirmado' 
                    ? 'rgba(34, 197, 94, 0.2)' 
                    : 'rgba(251, 191, 36, 0.2)'
                }`,
                borderRadius: 'var(--radius-lg)',
                background: `linear-gradient(135deg, ${
                  turnoSeleccionado?.estado?.toLowerCase() === 'confirmado' 
                    ? 'rgba(34, 197, 94, 0.05)' 
                    : 'rgba(251, 191, 36, 0.05)'
                } 0%, transparent 100%)`,
                backdropFilter: 'blur(10px)'
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <div className="tech-action-icon" style={{ 
                  width: '50px', 
                  height: '50px', 
                  fontSize: '20px',
                  background: turnoSeleccionado?.estado?.toLowerCase() === 'confirmado' 
                    ? 'var(--gradient-success)' 
                    : 'var(--gradient-warning)'
                }}>
                  <IonIcon icon={checkmarkCircleOutline} />
                </div>
                <Box flex={1}>
                  <Typography level="title-md" sx={{ 
                    color: 'var(--color-text-primary)',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    Estado del Turno
                  </Typography>
                  <div className={`tech-badge ${turnoSeleccionado?.estado?.toLowerCase() || 'pendiente'}`} style={{
                    fontSize: '1rem',
                    padding: '0.5rem 1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {turnoSeleccionado?.estado || 'Pendiente'}
                  </div>
                </Box>
              </Box>
            </Card>

            {/* Tipo de turno */}
            {turnoSeleccionado?.tipo && (
              <Card 
                className="tech-detalle-section"
                sx={{
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <div className="tech-action-icon" style={{ 
                    width: '50px', 
                    height: '50px', 
                    fontSize: '20px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  }}>
                    <IonIcon icon={documentTextOutline} />
                  </div>
                  <Box flex={1}>
                    <Typography level="title-md" sx={{ 
                      color: 'var(--color-text-primary)',
                      fontWeight: '600',
                      marginBottom: '0.25rem'
                    }}>
                      Tipo de Consulta
                    </Typography>
                    <Typography level="body-md" sx={{ 
                      color: '#7c3aed',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {turnoSeleccionado.tipo}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Botones de acción */}
            <Box 
              display="flex" 
              gap={2} 
              justifyContent="flex-end" 
              marginTop={3}
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                color="neutral"
                onClick={toggleModalDetalleTurno}
                sx={{
                  borderRadius: 'var(--radius-lg)',
                  padding: '0.75rem 1.5rem',
                  fontWeight: '600',
                  textTransform: 'none',
                  fontSize: '1rem',
                  minWidth: '120px'
                }}
              >
                Cerrar
              </Button>
              
              <Button
                variant="solid"
                onClick={() => {
                  toggleModalDetalleTurno();
                  handleClickComprobanteTurno(turnoSeleccionado);
                }}
                sx={{
                  background: 'var(--gradient-primary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '0.75rem 1.5rem',
                  fontWeight: '600',
                  textTransform: 'none',
                  fontSize: '1rem',
                  minWidth: '160px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0056b3 0%, #1e40af 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                <IonIcon icon={cloudDownloadOutline} style={{ marginRight: '0.5rem' }} />
                Comprobante
              </Button>
            </Box>
          </div>
        </ModalDialog>
      </Modal>

      {/* Sistema de notificaciones toast */}
      <CustomToast
        openToast={toast.open}
        message={toast.mensaje}
        colorNotificacion={toast.tipo}
        onDidDismiss={() => mostrarNotificacion(false, "", "")}
      />
    </div>
  );
}

export default MainPage;
